import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Alert,
  Container,
  Chip,
  Divider
} from '@mui/material';
import {
  PencilSimple,
  Trash,
  SignIn
} from '@phosphor-icons/react';
import api from '../../utils/api';
import { setGhostLoginReturn } from '../../utils/ghostLoginSession';
import useThemeStyles from '../../theme/useThemeStyles';

const PLACEHOLDER_EMAIL = 'public.embed.booking.placeholder@idotoo.local';

const canGhostLogin = (user) => {
  if (['admin', 'support'].includes(user.role)) return false;
  if (user.status === 'suspended') return false;
  const email = String(user.email || '').toLowerCase();
  if (email === PLACEHOLDER_EMAIL || email.endsWith('@idotoo.local')) return false;
  return true;
};

const Users = () => {
  const styles = useThemeStyles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);
  const [editForm, setEditForm] = useState({
    email: '',
    role: '',
    permissions: [],
    resetPassword: '',
    businessPlans: {}
  });
  const [plans, setPlans] = useState([]);
  const [banReason, setBanReason] = useState('');
  const [ghostLoadingId, setGhostLoadingId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setError(null);
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await api.get('/api/admin/plans');
      setPlans(res.data);
    } catch {
      setPlans([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPlans();
  }, []);

  useEffect(() => {
    if (!users.length || !location.hash?.startsWith('#user-')) return;
    const timer = setTimeout(() => {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [users, location.hash]);

  const handleGhostLogin = async (user) => {
    const label = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    if (!window.confirm(`Log in as ${label}? You will leave the admin panel.`)) {
      return;
    }

    setGhostLoadingId(user._id);
    setError(null);

    try {
      const adminToken = localStorage.getItem('token');
      const adminUserType = localStorage.getItem('userType');
      const response = await api.post(`/api/admin/users/${user._id}/ghost-login`);

      setGhostLoginReturn({ token: adminToken, userType: adminUserType || 'admin' });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', response.data.userType);
      localStorage.removeItem('weddingId');
      localStorage.removeItem('currentBusinessId');

      if (response.data.wedding?._id) {
        localStorage.setItem('weddingId', response.data.wedding._id);
      }

      api.defaults.headers.common['x-auth-token'] = response.data.token;
      window.dispatchEvent(new CustomEvent('authChange'));
      window.location.href = response.data.redirectPath;
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start ghost login. Please try again.');
      setGhostLoadingId(null);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    const businessPlans = {};
    (user.businesses || []).forEach((b) => {
      businessPlans[b._id] = b.plan || 'supplier_free';
    });
    setEditForm({
      email: user.email || '',
      role: user.role,
      permissions: user.permissions || [],
      resetPassword: '',
      businessPlans
    });
    setEditDialog(true);
  };

  const handleEditClose = () => {
    setEditDialog(false);
    setSelectedUser(null);
    setEditForm({ email: '', role: '', permissions: [], resetPassword: '', businessPlans: {} });
    setBanReason('');
  };

  const handleEditSubmit = async () => {
    try {
      setError(null);
      const payload = {
        email: editForm.email?.trim(),
        role: editForm.role,
        permissions: editForm.permissions
      };
      if (editForm.resetPassword && editForm.resetPassword.trim()) {
        if (editForm.resetPassword.length < 8) {
          setError('Password must be at least 8 characters.');
          return;
        }
        payload.newPassword = editForm.resetPassword.trim();
      }
      await api.put(`/api/admin/user/${selectedUser._id}`, payload);

      for (const b of selectedUser.businesses || []) {
        const newPlan = editForm.businessPlans[b._id];
        if (newPlan && newPlan !== b.plan) {
          if (b.subscriptionId) {
            await api.put(`/api/admin/business-subscriptions/${b.subscriptionId}/plan`, {
              membershipLevelKey: newPlan
            });
          } else {
            await api.put(`/api/admin/business/${b._id}/subscription/plan`, {
              membershipLevelKey: newPlan
            });
          }
        }
      }

      await fetchUsers();
      handleEditClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user. Please try again.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        setError(null);
        const _response = await api.delete(`/api/admin/user/${userId}`);
        await fetchUsers();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to delete user. Please try again.';
        setError(errorMessage);
      }
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      setError(null);
      await api.post(`/api/admin/ban-user/${selectedUser._id}`, {
        reason: banReason.trim() || undefined
      });
      await fetchUsers();
      setBanReason('');
      handleEditClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to ban user.');
    }
  };

  const handleUnbanUser = async () => {
    if (!selectedUser) return;
    try {
      setError(null);
      await api.post(`/api/admin/unban-user/${selectedUser._id}`);
      await fetchUsers();
      handleEditClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unban user.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Linked Business</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', width: '1%' }}>Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id} id={`user-${user._id}`}>
                <TableCell>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  {user.businesses?.length > 0 ? (
                    user.businesses.map((business) => (
                      <Chip
                        key={business._id}
                        label={business.name}
                        size="small"
                        clickable
                        onClick={() => navigate('/admin/suppliers')}
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  <Typography component="span" color={user.status === 'suspended' ? 'error.main' : 'text.primary'}>
                    {user.status === 'suspended' ? 'Banned' : 'Active'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', width: '1%' }}>
                  {canGhostLogin(user) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        ghostLoadingId === user._id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SignIn size={16} />
                        )
                      }
                      disabled={ghostLoadingId === user._id}
                      onClick={() => handleGhostLogin(user)}
                      sx={{
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        '& .MuiButton-startIcon': { marginRight: 0.75 }
                      }}
                    >
                      Log in as
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditClick(user)} title="Edit user">
                    <PencilSimple size={18} />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteUser(user._id)}
                    color="error"
                    title="Delete user"
                  >
                    <Trash size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editDialog} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              <TextField
                fullWidth
                margin="normal"
                type="email"
                label="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />

              <TextField
                select
                fullWidth
                margin="normal"
                label="Role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>

              <TextField
                fullWidth
                margin="normal"
                type="password"
                label="Reset password"
                placeholder="Leave blank to keep current password"
                value={editForm.resetPassword}
                onChange={(e) => setEditForm({ ...editForm, resetPassword: e.target.value })}
                autoComplete="new-password"
              />

              {selectedUser.role === 'supplier' && (selectedUser.businesses?.length > 0) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Plan (per business)
                  </Typography>
                  {(selectedUser.businesses || []).map((b) => (
                    <Box key={b._id} sx={{ mb: 2 }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {b.name}
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={editForm.businessPlans[b._id] || b.plan || 'supplier_free'}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            businessPlans: {
                              ...editForm.businessPlans,
                              [b._id]: e.target.value
                            }
                          })
                        }
                      >
                        {(plans.length ? plans : [
                          { key: 'supplier_free', name: 'Free' },
                          { key: 'supplier_free_trial', name: 'Free Trial' },
                          { key: 'supplier_individual', name: 'Individual' },
                          { key: 'supplier_team', name: 'Team' },
                          { key: 'supplier_enterprise', name: 'Enterprise' }
                        ]).map((p) => (
                          <MenuItem key={p.key} value={p.key}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ))}
                </>
              )}

              {selectedUser.role !== 'admin' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ban / Unban
                  </Typography>
                  {selectedUser.status === 'suspended' ? (
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={handleUnbanUser}
                      sx={{ mt: 1 }}
                    >
                      Unban user
                    </Button>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        label="Reason for ban"
                        multiline
                        rows={2}
                        value={banReason}
                        onChange={(e) => setBanReason(e.target.value)}
                        fullWidth
                        placeholder="e.g. Terms violation, abuse"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleBanUser}
                        disabled={!banReason.trim()}
                      >
                        Ban user
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default Users; 